import {
  Component,
  QueryList,
  ViewChildren,
  OnDestroy,
  ChangeDetectorRef,
  ElementRef,
  Renderer2,
  AfterViewInit,
  OnInit,
  DoCheck,
  AfterContentInit,
  AfterContentChecked,
  AfterViewChecked,
  OnChanges,
  SimpleChanges,
  Injector,
} from "@angular/core";
import { Router, NavigationEnd } from "@angular/router";
import { filter, map, distinctUntilChanged, skip } from "rxjs/operators"; // <-- ADDED skip
import { firstValueFrom, Observable, Subscription, from } from "rxjs";
import { Platform, IonRouterOutlet, ModalController } from "@ionic/angular";
import {
  PushNotifications,
  Token,
  ActionPerformed,
  PushNotificationSchema,
} from "@capacitor/push-notifications";
import { Capacitor } from "@capacitor/core";
import { TranslateService } from "@ngx-translate/core";
import { register } from "swiper/element/bundle";
register();

import { AuthService } from "./services/auth.service";
import { ConfigService } from "./provider/config.service";
import { DataService } from "./services/data.service";
import { RouterStateTrackerService } from "./services/router-state-tracker.service";
import { ThemeService } from "./services/theme.service";
import { DailyMenuDataService } from "./services/daily-menu-data.service";
import { SubscriptionDateService } from "./services/subscription-date.service";
import { KeyboardService } from "./services/keyboard.service";
import { CacheManagementService } from "./services/cache-management.service";
import moment from "moment";

type SlidePosition =
  | "/calendar"
  | "/menu"
  | "/order"
  | "/notifications"
  | "/accounts"
  | any;

@Component({
  selector: "app-root",
  templateUrl: "app.component.html",
  styleUrls: ["app.component.scss"],
  standalone: false,
})
export class AppComponent implements OnDestroy {
  @ViewChildren(IonRouterOutlet) routerOutlets!: QueryList<IonRouterOutlet>;

  public connectedToInternet: boolean = true;
  public backButton = true;
  public settings: any = {};
  public currentRouteValue!: string;
  public currentActiveRoute!: string;
  public headSize: string = "";
  today: string = moment().format("YYYY-MM-DD");
  private subscription = new Subscription();
  mainProfile?: Observable<any>;
  customer: any = null;
  onlineOrders: boolean = false;
  storeData?: any;
  isCelebrityFeatureEnabled?: boolean;
  dialCode?: any;
  isDateWiseMenuEnabled?: number;
  userPhoneAllowedDigit?: any;
  isReferralEnabled?: boolean;
  splashScreen: boolean = true;
  currentPos: string = "";
  previousPos: string = "";
  tabAllow: boolean = true;
  pushToken?: string;
  tabBarClass: string = "hiddenBlur";
  macrosBarClass: string = "hideMacros";
  blurClass: string = "smallBlurrer2";
  isUserAuthenticated: boolean = false;

  constructor(
    private router: Router,
    private platform: Platform,
    private modalCtrl: ModalController,
    private translate: TranslateService,
    private authService: AuthService,
    private configService: ConfigService,
    private dataService: DataService,
    private routerStateTrackerService: RouterStateTrackerService,
    public themeService: ThemeService,
    private changeDetectorRef: ChangeDetectorRef,
    private dailyMenuDataService: DailyMenuDataService,
    private subscriptionDateService: SubscriptionDateService,
    private keyboardService: KeyboardService,
    private cacheManagementService: CacheManagementService,
    private injector: Injector
  ) {
    this.cacheManagementService.setInjector(this.injector);
    this.initializeApp();
    this.setupRouterTracking();
    this.initializeKeyboard();
  }

  async initializeKeyboard() {
    this.platform.ready().then(async () => {
      await this.keyboardService.initialize();

      this.keyboardService.setConfigs([
        {
          containerId: "signinKeyboardWrapper",
          inputId: "*",
          transform: "",
          useViewportLift: true,
        },
        {
          containerId: "signupKeyboardWrapper",
          inputId: "*",
          transform: "",
          useViewportLift: true,
        },
        {
          containerId: "containerSignUp",
          inputId: "signupEmailInput",
          transform: "translateY(-100px)",
        },
        {
          containerId: "containerSignUp",
          inputId: "signupAreaInput",
          transform: "translateY(-150px)",
        },
        {
          containerId: "containerSignUp",
          inputId: "signupReferralInput",
          transform: "translateY(-200px)",
        },
        {
          containerId: "containerSignUp",
          inputId: "signupPasswordInput",
          transform: "translateY(-250px)",
        },
        {
          containerId: "addressFormContainer",
          inputId: "addressNameInput",
          transform: "translateY(-50px)",
        },
        {
          containerId: "addressFormContainer",
          inputId: "addressPhoneInput",
          transform: "translateY(-50px)",
        },
        {
          containerId: "addressFormContainer",
          inputId: "addressAreaInput",
          transform: "translateY(-100px)",
        },
        {
          containerId: "addressFormContainer",
          inputId: "addressSlotInput",
          transform: "translateY(-100px)",
        },
        {
          containerId: "addressFormContainer",
          inputId: "addressBlockInput",
          transform: "translateY(-200px)",
        },
        {
          containerId: "addressFormContainer",
          inputId: "addressAvenueInput",
          transform: "translateY(-200px)",
        },
        {
          containerId: "addressFormContainer",
          inputId: "addressStreetInput",
          transform: "translateY(-250px)",
        },
        {
          containerId: "addressFormContainer",
          inputId: "addressHouseInput",
          transform: "translateY(-250px)",
        },
        {
          containerId: "addressFormContainer",
          inputId: "addressFloorInput",
          transform: "translateY(-300px)",
        },
        {
          containerId: "addressFormContainer",
          inputId: "addressFlatInput",
          transform: "translateY(-300px)",
        },
        {
          containerId: "addressFormContainer",
          inputId: "addressLandmarkInput",
          transform: "translateY(-350px)",
        },
        {
          containerId: "addressFormContainer",
          inputId: "addressZipInput",
          transform: "translateY(-400px)",
        },
        {
          containerId: "addressFormContainer",
          inputId: "addressPaciInput",
          transform: "translateY(-400px)",
        },
        {
          containerId: "addressFormContainer",
          inputId: "addressDriverNoteInput",
          transform: "translateY(-450px)",
        },
        {
          containerId: "addressFormContainer",
          inputId: "addressNotesInput",
          transform: "translateY(-500px)",
        },
      ]);
    });
  }

  onSlide(event: { current: SlidePosition; previous: SlidePosition }) {
    if (event.current !== event.previous) {
      this.routerStateTrackerService.navigateWithPreExitAnimation(
        event.current
      );
    }
  }

  private async initializeUserServices(user: any): Promise<void> {
    if (!user?.id) {
      console.warn("[App] Cannot initialize services: no user ID");
      return;
    }

    try {
      // PHASE 1: Initialize SubscriptionDateService FIRST (master service)
      // This service is the single source of truth for subscription data
      await this.subscriptionDateService.initialize(user.id);

      // PHASE 2: Initialize dependent services in parallel
      // These services depend on SubscriptionDateService being ready
      await Promise.all([
        this.dailyMenuDataService.initializeCache(user.id),
        // DataService will now use SubscriptionDateService instead of direct API calls
        firstValueFrom(this.dataService.loadUserData()),
      ]);

      // PHASE 3: Preload menu data for next 3 available dates
      const state = this.subscriptionDateService.getState();
      console.log("[App] Subscription state after init:", {
        availableDatesCount: state.availableDates.length,
        firstThreeDates: state.availableDates.slice(0, 3),
        hasCustomer: !!this.customer,
      });

      if (state.availableDates.length > 0) {
        console.log("[App] Starting menu preload...");
        try {
          await this.dailyMenuDataService.preloadInitialDays(
            user.id,
            this.customer || user
          );
          console.log("[App] Menu preload completed successfully");
        } catch (err) {
          console.error("[App] Preload error:", err);
        }
      } else {
        console.log("[App] No available dates for preload");
      }
    } catch (error) {
      console.error(
        "[App] Critical error during service initialization:",
        error
      );
      // Don't throw - allow app to continue with degraded functionality
    }
  }

  async initializeApp() {
    this.splashScreen = true;

    try {
      // Load configurations ONCE
      let configData: any = null;
      try {
        configData = await firstValueFrom(
          this.dataService.getData("configurations")
        );
      } catch (configError) {
        console.error("⚠️ Configuration load failed:", configError);
      }

      await new Promise((res) => setTimeout(res, 1800)); // 1800ms splash screen

      this.splashScreen = false;

      // Initialize theme and cache config data
      if (configData) {
        try {
          await this.themeService.initializeTheme(configData);
          await this.configService.cacheApiConfig(configData);
        } catch (themeError) {
          console.warn("⚠️ Theme initialization failed:", themeError);
        }
      }

      // Restore previous verified session automatically
      // const isSessionRestored = await this.authService.restoreSession();
      // if (isSessionRestored) {
        // console.log("isCaleld");
        // await this.routerStateTrackerService.navigateWithPreExitAnimation('/calendar', { state: 'restored' });
      // } else {
        // await this.routerStateTrackerService.navigateWithPreExitAnimation('/onboarding', { state: 'fresh' });
      // }

      // --- Subscribe to authentication state changes ---
      this.subscription.add(
        this.authService.isAuthenticated$
          .pipe(distinctUntilChanged())
          .subscribe((isAuthenticated: boolean) => {
            this.isUserAuthenticated = isAuthenticated;
            this.updateBarClasses();
            this.changeDetectorRef.detectChanges();
          })
      );

      // --- CENTRALIZED AUTH HANDLING - Single subscription manages all auth state ---
      this.subscription.add(
        this.authService.currentUser$
          .pipe(distinctUntilChanged())
          .subscribe(async (user: any | null) => {
            // User is authenticated and verified (API is source of truth)
            if (user?.id && user?.is_verified === true) {
              this.customer = user;
              // Use new orchestrated initialization
              await this.initializeUserServices(this.customer);
              return;
            }

            // User exists but not verified (waiting for OTP)
            if (user?.id && user?.is_verified === false) {
              // this.customer = user;
              this.customer = null;
              await this.routerStateTrackerService.navigateWithPreExitAnimation(
                "/onboarding",
                { state: "unauthenticated" }
              );
              return;
            }

            // User logged out or session expired - CENTRALIZED CLEANUP
            this.customer = null;

            // Clear all user-specific data
            this.cacheManagementService.clearAllUserData();
            this.subscriptionDateService.clearCache();
            this.dataService.clearUserSpecificData();

            // Navigate to onboarding unless already there or on OTP page
            const currentUrl = this.router.url;
            if (
              !currentUrl.includes("/onboarding") &&
              !currentUrl.includes("/otp-verification")
            ) {
              await this.routerStateTrackerService.navigateWithPreExitAnimation(
                "/onboarding",
                { state: "unauthenticated" }
              );
            }
          })
      );

      // --- Subscribe to app settings ---
      this.subscription.add(
        this.configService.appSettings$
          .pipe(
            // Skip initial emission since APP_INITIALIZER already set language/direction
            skip(1)
          )
          .subscribe((settings: any) => {
            this.settings = settings;
            if (this.settings.appLanguage) {
              // Only update if settings actually changed (e.g., user changed language)
              if (
                document.documentElement.lang !== this.settings.appLanguage ||
                document.documentElement.dir !== this.settings.appDirection
              ) {
                document.documentElement.lang = this.settings.appLanguage;
                document.documentElement.dir = this.settings.appDirection;
                this.translate.setDefaultLang(this.settings.appLanguage);
                this.translate.use(this.settings.appLanguage);
              }
            }
            this.changeDetectorRef.detectChanges();
          })
      );

      // --- Subscribe to header size changes ---
      this.subscription.add(
        this.themeService.currentheaderSize$.subscribe((value: any) => {
          this.headSize = value;
          this.changeDetectorRef.detectChanges();
        })
      );
    } catch (error) {
      console.error("🔴 App initialization error:", error);
      this.splashScreen = false;
    }
  }
  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.keyboardService.destroy();
  }
  setupRouterTracking() {
    const routerEvents$ = this.router.events?.pipe(
      filter((event: any) => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects)
    );

    this.subscription.add(
      routerEvents$.subscribe((url: any) => {
        this.currentActiveRoute = url;
        this.currentRouteValue = url;
        this.updateBarClasses();
        this.changeDetectorRef.detectChanges();
      })
    );

    this.currentRouteValue = this.router.url;
    this.updateBarClasses();
  }
  setupPushNotifications(): void {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    if (!Capacitor.isPluginAvailable("PushNotifications")) {
      return;
    }

    PushNotifications.requestPermissions().then((result: any) => {
      if (result.receive === "granted") {
        PushNotifications.register();
      } else {
        PushNotifications.register();
      }
    });

    PushNotifications.addListener("registration", (token: Token) => {
      this.pushToken = token.value;
    });

    PushNotifications.addListener("registrationError", (error: any) => {
      console.error("Push registration error:", error);
    });

    PushNotifications.addListener(
      "pushNotificationReceived",
      (_notification: PushNotificationSchema) => {}
    );
    PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (_notification: ActionPerformed) => {}
    );
  }
  updateBarClasses(): void {
    this.onlineOrders = true;
    const tabRoutes = ["/calendar", "/menu", "/order", "/accounts"];
    const isTabRoute = tabRoutes.includes(this.currentActiveRoute);

    if (!this.isUserAuthenticated || !isTabRoute) {
      this.tabBarClass = "hiddenBlur";
    } else {
      this.tabBarClass = "fullTab";
    }
  }
  getBarClass(element: string): string {
    return element === "tab" ? this.tabBarClass : "";
  }
}
